import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { Navbar } from "@/components/Navbar";
import { Footer } from "@/components/Footer";
import { WhatsAppFloat } from "@/components/WhatsAppFloat";
import { Seo } from "@/components/Seo";
import { Breadcrumbs } from "@/components/Breadcrumbs";
import { MapPinned, ChevronRight } from "lucide-react";

const MAIN_PAGES = [
  { to: "/", label: "Beranda" },
  { to: "/rute", label: "Semua Rute & Kota" },
  { to: "/kirim-paket", label: "Kirim Paket Kilat" },
  { to: "/blog", label: "Blog" },
  { to: "/faq", label: "FAQ" },
  { to: "/lacak-order", label: "Lacak Order" },
  { to: "/tentang-kami", label: "Tentang Kami" },
  { to: "/syarat-ketentuan", label: "Syarat & Ketentuan" },
  { to: "/kebijakan-privasi", label: "Kebijakan Privasi" },
];

const Section = ({ title, children }) => (
  <section className="mt-10">
    <h2 className="mb-3 font-display text-xl font-bold tracking-tight">{title}</h2>
    {children}
  </section>
);

export default function SitemapPage() {
  const [cities, setCities] = useState([]);
  const [popular, setPopular] = useState([]);
  const [articles, setArticles] = useState([]);
  const [drivers, setDrivers] = useState([]);

  useEffect(() => {
    api.get("/cities").then((r) => setCities(r.data)).catch(() => {});
    api.get("/routes/popular").then((r) => setPopular(r.data)).catch(() => {});
    api.get("/articles").then((r) => setArticles(r.data)).catch(() => {});
    api.get("/drivers/list").then((r) => setDrivers(r.data)).catch(() => {});
  }, []);

  const byProv = cities.reduce((acc, c) => {
    const p = c.province || "Lainnya";
    (acc[p] = acc[p] || []).push(c);
    return acc;
  }, {});
  const provinces = Object.keys(byProv).sort();

  return (
    <div className="min-h-screen bg-background" data-testid="sitemap-page">
      <Seo
        path="/sitemap"
        title="Peta Situs (Sitemap) | Travele"
        description="Peta situs Travele — daftar lengkap semua halaman: kota layanan, rute travel door to door & charter drop off, blog, dan halaman informasi."
      />
      <Navbar />
      <Breadcrumbs items={[{ name: "Beranda", path: "/" }, { name: "Peta Situs" }]} />

      <div className="mx-auto max-w-7xl px-4 py-10 sm:px-6">
        <p className="font-mono-terminal text-xs uppercase tracking-widest text-primary">Peta Situs</p>
        <h1 className="mt-2 font-display text-3xl font-black uppercase tracking-tighter sm:text-4xl">Sitemap HTML</h1>
        <p className="mt-3 max-w-3xl leading-relaxed text-muted-foreground">
          Daftar lengkap seluruh halaman Travele untuk memudahkan Anda dan mesin pencari menjelajah situs.
          Versi XML tersedia di <a href="/api/sitemap.xml" className="text-primary hover:underline" data-testid="sitemap-xml-link">/api/sitemap.xml</a>.
        </p>

        <Section title="Halaman Utama">
          <ul className="grid gap-2 sm:grid-cols-2 lg:grid-cols-3">
            {MAIN_PAGES.map((p) => (
              <li key={p.to}>
                <Link to={p.to} className="flex items-center gap-1 text-sm text-primary hover:underline" data-testid={`sitemap-main-${p.to}`}>
                  <ChevronRight className="h-3 w-3" /> {p.label}
                </Link>
              </li>
            ))}
          </ul>
        </Section>

        {popular.length > 0 && (
          <Section title="Rute Populer">
            <ul className="grid gap-2 sm:grid-cols-2 lg:grid-cols-3">
              {popular.map((r) => (
                <li key={r.slug}>
                  <Link to={`/${r.slug}`} className="flex items-center gap-1 text-sm text-primary hover:underline">
                    <ChevronRight className="h-3 w-3" /> {r.origin} → {r.destination}
                  </Link>
                </li>
              ))}
            </ul>
          </Section>
        )}

        <Section title="Kota Layanan">
          {provinces.length === 0 && <p className="text-sm text-muted-foreground">Memuat daftar kota...</p>}
          {provinces.map((prov) => (
            <div key={prov} className="mt-4">
              <h3 className="mb-2 flex items-center gap-2 font-display text-base font-bold"><MapPinned className="h-4 w-4 text-primary" /> {prov}</h3>
              <ul className="grid gap-1.5 sm:grid-cols-2 lg:grid-cols-3">
                {byProv[prov].map((c) => (
                  <li key={c.slug} className="flex flex-wrap gap-x-3 text-sm">
                    <Link to={`/travel-dari-${c.slug}`} className="text-primary hover:underline">Travel dari {c.name}</Link>
                    <Link to={`/travel-ke-${c.slug}`} className="text-muted-foreground hover:text-primary hover:underline">ke {c.name}</Link>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </Section>

        {drivers.length > 0 && (
          <Section title="Driver / PO Travel">
            <ul className="grid gap-2 sm:grid-cols-2 lg:grid-cols-3">
              {drivers.map((d) => (
                <li key={d.slug}>
                  <Link to={`/driver/${d.slug}`} className="flex items-center gap-1 text-sm text-primary hover:underline">
                    <ChevronRight className="h-3 w-3" /> {d.business_name}{d.city ? ` — ${d.city}` : ""}
                  </Link>
                </li>
              ))}
            </ul>
          </Section>
        )}

        {articles.length > 0 && (
          <Section title="Artikel Blog">
            <ul className="grid gap-2 sm:grid-cols-2">
              {articles.map((a) => (
                <li key={a.slug}>
                  <Link to={`/blog/${a.slug}`} className="flex items-center gap-1 text-sm text-primary hover:underline">
                    <ChevronRight className="h-3 w-3" /> {a.title}
                  </Link>
                </li>
              ))}
            </ul>
          </Section>
        )}
      </div>

      <Footer />
      <WhatsAppFloat />
    </div>
  );
}
